import React from 'react';

import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';

import { RefundPreviewMethod } from '~/services/PaymentsService';

import ApiError from '~/types/ApiError';

import currencyFormatter from '~/utils/currencyFormatter';

const REFUND_METHOD_LABELS: Record<string, string> = {
  credit: 'LE Credits',
  le_points: 'LE Points',
  le_points_benefits: 'LE Points benefits',
  le_points_classic_reward: 'Classic Reward',
  promo: 'Promo',
  voucher: 'Voucher',
  qantas: 'Qantas Points',
  velocity: 'Velocity Points',
  flying_blue: 'Flying Blue',
  gift_card: 'Gift Card',
  stripe: 'Stripe',
  braintree: 'Braintree',
  paypal: 'PayPal',
  razorpay: 'Razorpay',
  zip: 'Zip',
  latitude: 'Latitude',
  latitude_pay: 'LatitudePay',
  klarna: 'Klarna',
  bridgerpay: 'BridgerPay',
  partner: 'Partner',
  azupay_pay_id: 'Azupay PayID',
  manual: 'Offline/Manual',
  'manual-latitude': 'Offline/Manual - Latitude',
};

interface Props {
  currencyCode: string;
  methods?: Array<RefundPreviewMethod>;
  isLoading?: boolean;
  error?: ApiError | null;
}

function formatRefundMethodLabel(type: string): string {
  return REFUND_METHOD_LABELS[type] ?? type.replace(/_/g, ' ');
}

export default function RefundPaymentBreakdown({ currencyCode, methods = [], isLoading = false, error }: Props) {
  const rows = methods.filter((method) => Number(method.amount) !== 0);

  if (isLoading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} data-testid="refund-payment-breakdown-loading">
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading payment breakdown
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" data-testid="refund-payment-breakdown-error">
        Unable to load payment breakdown.
      </Alert>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <Box data-testid="refund-payment-breakdown">
      <Typography fontWeight="bold" gutterBottom>
        Payment breakdown
      </Typography>
      <Stack spacing={0.75}>
        {rows.map((method) => (
          <Stack
            key={method.type}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            data-testid={`refund-payment-breakdown-${method.type}`}
          >
            <Typography variant="body2">{formatRefundMethodLabel(method.type)}</Typography>
            <Typography variant="body2">{currencyFormatter(currencyCode, method.amount, 2)}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
